<script lang="ts" setup>
const isShowSizesModal = defineModel();
const countInCart = defineModel('countInCart', { default: 0 });

const props = defineProps<{
  isInModal?: boolean;
  isAvailableOnlyOffline: boolean | null;
  oldPrice?: number | null;
  price?: number;
  selectedOfferAvailableCount: number | null;
  recommendationsBlockId?: string;
}>();

const emits = defineEmits(['added-to-cart']);

const cartStore = useCartStore();
const catalogStore = useCatalogStore();
const { selectedOffer } = storeToRefs(catalogStore);

const displayedPrice = computed(() => props.price ?? selectedOffer.value.price);
const displayedOldPrice = computed(() =>
  props.oldPrice === undefined ? selectedOffer.value.old_price : props.oldPrice,
);
const isPriceUnavailable = computed(() => {
  const price = Number(displayedPrice.value);

  return !Number.isFinite(price) || price <= 0;
});

const isNotAvailableToIncrement = computed(() => {
  if (props.selectedOfferAvailableCount) return countInCart.value >= props.selectedOfferAvailableCount;
});

const route = useRoute();
const isLoading = ref('');
const { sendOfferEventToDataLayer } = useGtmModule();
const { isMobile } = useBreakpoints();
const selectedCartOfferId = computed(() => catalogStore.selectedSizeId ?? catalogStore.selectedOffer.id);

async function addToCart() {
  if (isLoading.value) return;
  if (isPriceUnavailable.value) return;
  if (countInCart.value) return navigateTo('/cart');
  if (props.selectedOfferAvailableCount === null) {
    isShowSizesModal.value = true;
    return;
  }

  isLoading.value = 'increment';

  const isAddedToCart = await cartStore.addToCart(selectedCartOfferId.value);

  if (isAddedToCart) {
    emits('added-to-cart');
    await sendOfferEventToDataLayer('add_to_cart', catalogStore.selectedOffer);
    window.roistat?.event?.send('add_to_cart');
    if (window.r46) {
      const params = route.query.recommended_code?.length
        ? {
            id: selectedCartOfferId.value,
            recommended_by: 'dynamic',
            recommended_code: route.query.recommended_code,
            amount: 1,
          }
        : { id: selectedCartOfferId.value, amount: 1 };

      window.r46('track', 'cart', params);
    }
  }

  isLoading.value = '';
}

async function incrementCountInCart() {
  if (isLoading.value) return;

  isLoading.value = 'increment';

  await cartStore.increment(selectedCartOfferId.value);

  await sendOfferEventToDataLayer('add_to_cart', catalogStore.selectedOffer);
  window.roistat?.event?.send('add_to_cart');
  if (window.r46) {
    const item = cartStore.countedCartItems.find((i) => i.id === selectedCartOfferId.value);
    const params = route.query.recommended_code
      ? {
          id: selectedCartOfferId.value,
          recommended_by: 'dynamic',
          recommended_code: route.query.recommended_code,
          amount: item?.count,
        }
      : { id: selectedCartOfferId.value, amount: item?.count };

    window.r46('track', 'cart', params);
  }

  isLoading.value = '';
}

async function decrementCountInCart() {
  if (isLoading.value) return;

  isLoading.value = 'decrement';

  await cartStore.decrement(selectedCartOfferId.value);

  if (countInCart.value === 0) {
    await sendOfferEventToDataLayer('remove_from_cart', catalogStore.selectedOffer);
    window.roistat?.event?.send('remove_from_cart');
  }

  isLoading.value = '';
}
</script>

<template>
  <div :class="{ 'product-page-cart-btn': !isInModal }">
    <div class="flex gap-2 w-full">
      <div
        v-if="isMobile && !isInModal"
        :class="{ 'product-page-cart-btn__price--with-count-in-cart': countInCart }"
        class="product-page-cart-btn__price"
      >
        <div
          v-if="displayedOldPrice && !isPriceUnavailable"
          class="line-through text-sm leading-none text-black-60"
        >
          {{ Number(displayedOldPrice).toLocaleString('ru-RU') }}&nbsp;₽
        </div>

        <h2 class="text-base leading-none flex items-center gap-1.5">
          <template v-if="!isPriceUnavailable">{{ Number(displayedPrice).toLocaleString('ru-RU') }}&nbsp;₽</template>
          <template v-else>Цена уточняется</template>

          <LoyaltyBadge
            v-if="isMobile && !isPriceUnavailable"
            :amount="selectedOffer.loyalty"
            :class="{ 'product-page-cart-btn__loyalty-badge--with-count-in-cart': countInCart }"
            class="product-page-cart-btn__loyalty-badge"
          />
        </h2>
      </div>

      <TheBtn
        :append-icon="countInCart ? '' : 'Cart'"
        :color="countInCart ? 'brick' : 'accent'"
        :disabled="
          isPriceUnavailable ||
          isAvailableOnlyOffline ||
          (selectedOfferAvailableCount !== null && Number(selectedOfferAvailableCount) < 1)
        "
        :title="isPriceUnavailable ? 'Товар временно недоступен для покупки' : ''"
        :is-loading="Boolean(isLoading)"
        class="product-page-cart-btn__element"
        @click="addToCart"
      >
        <div class="flex flex-col gap-1">
          {{ countInCart ? 'В&nbsp;корзине' : 'В&nbsp;корзину' }}
        </div>
      </TheBtn>

      <div
        v-if="countInCart"
        class="flex items-center"
      >
        <ConfirmationDropdown
          v-if="countInCart === 1"
          cancelBtnText="Нет"
          okBtnText="Да"
          text="Удалить из корзины?"
          @confirm="decrementCountInCart"
        >
          <TheBtn
            :disabled="Boolean(isLoading)"
            class="product-page-cart-btn__element-square"
            color="secondary"
          >
            <Transition
              v-if="isLoading === 'decrement'"
              name="loader"
            >
              <SvgoSpinner
                class="animate-spin text-xl"
                title="Идет загрузка..."
              />
            </Transition>

            <span
              v-else
              class="text-2xl mb-1"
            >
              –
            </span>
          </TheBtn>
        </ConfirmationDropdown>

        <TheBtn
          v-else
          :disabled="Boolean(isLoading)"
          class="product-page-cart-btn__element-square"
          color="secondary"
          @click="decrementCountInCart"
        >
          <Transition
            v-if="isLoading === 'decrement'"
            name="loader"
          >
            <SvgoSpinner
              class="animate-spin text-xl"
              title="Идет загрузка..."
            />
          </Transition>

          <span
            v-else
            class="text-2xl mb-1"
          >
            –
          </span>
        </TheBtn>

        <span class="px-3">{{ countInCart }}</span>

        <TheBtn
          :disabled="Boolean(isLoading) || isNotAvailableToIncrement"
          :title="isNotAvailableToIncrement ? 'Больше нет в наличии' : ''"
          class="product-page-cart-btn__element-square"
          color="secondary"
          @click="incrementCountInCart"
        >
          <Transition
            v-if="isLoading === 'increment'"
            name="loader"
          >
            <SvgoSpinner
              class="animate-spin text-xl"
              title="Идет загрузка..."
            />
          </Transition>

          <span
            v-else
            class="text-2xl mb-1"
          >
            +
          </span>
        </TheBtn>
      </div>
    </div>

    <div class="text-xs text-center text-black-60 md:hidden">
      <span v-if="isAvailableOnlyOffline"> Товар доступен только для покупки в розничном магазине </span>

      <span v-else-if="selectedOfferAvailableCount !== null && selectedOfferAvailableCount < 1">
        Товара сейчас нет в наличии
      </span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.product-page-cart-btn {
  position: fixed;
  bottom: 74px;
  left: 0;
  z-index: 5;
  display: flex;
  flex-direction: column;
  gap: 6px;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 12px;
  background-color: $white;
  box-shadow: 0 0 20px rgb(0 0 0 / 15%);

  @include screen-md {
    position: static;
    width: 100%;
    padding: unset;
    background-color: unset;
    box-shadow: unset;
  }

  &__element {
    width: 100%;
    height: 40px;

    @include screen-sm {
      height: 48px;
    }
  }

  &__element-square {
    width: 40px;
    height: 40px;
    padding: 0;

    @include screen-sm {
      width: 48px;
      height: 48px;
    }
  }

  &__price {
    display: flex;
    flex-shrink: 0;
    flex-direction: column;
    justify-content: space-evenly;
    margin-right: 8px;

    &--with-count-in-cart {
      display: none;

      @media screen and (width >= 360px) {
        display: flex;
      }
    }
  }

  &__loyalty-badge {
    display: flex;

    &--with-count-in-cart {
      display: none;

      @media screen and (width >= 385px) {
        display: flex;
      }
    }
  }
}
</style>
